"use client";

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { createClient, isSupabaseConfigured } from "@/lib/supabase/client";

export type AdPlatform = "meta" | "google";

export interface AdAccount {
  id: string;
  meta_account_id: string;
  name: string;
  currency: string;
  timezone: string;
  status: string;
  platform: AdPlatform;
  access_token?: string;
  created_at: string;
}

interface AccountContextValue {
  accounts: AdAccount[];
  activeAccount: AdAccount | null;
  setActiveAccount: (account: AdAccount) => void;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const DEV_ACCOUNTS: AdAccount[] = [
  {
    id: "dev-account-meta",
    meta_account_id: "act_000000000",
    name: "Dev Meta Account",
    currency: "BRL",
    timezone: "America/Sao_Paulo",
    status: "active",
    platform: "meta",
    created_at: new Date().toISOString(),
  },
  {
    id: "dev-account-google",
    meta_account_id: "000-000-0000",
    name: "Dev Google Account",
    currency: "BRL",
    timezone: "America/Sao_Paulo",
    status: "active",
    platform: "google",
    created_at: new Date().toISOString(),
  },
];

const STORAGE_KEY = "smartads-active-account-id";

const AccountContext = createContext<AccountContextValue>({
  accounts: [],
  activeAccount: null,
  setActiveAccount: () => {},
  isLoading: true,
  refetch: async () => {},
});

export function AccountProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [activeAccount, setActiveAccountState] = useState<AdAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try {
      if (!isSupabaseConfigured) {
        setAccounts(DEV_ACCOUNTS);
        const savedId = localStorage.getItem(STORAGE_KEY);
        const saved = DEV_ACCOUNTS.find((a) => a.id === savedId);
        setActiveAccountState(saved ?? DEV_ACCOUNTS[0]);
        setIsLoading(false);
        return;
      }

      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAccounts([]);
        setActiveAccountState(null);
        setIsLoading(false);
        return;
      }

      // RLS automatically returns owned + shared accounts
      const { data } = await supabase
        .from("ad_accounts")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false });

      const allAccounts: AdAccount[] = (data ?? []).map((a: Record<string, unknown>) => ({
        id: a.id as string,
        meta_account_id: (a.meta_account_id ?? a.platform_account_id ?? "") as string,
        name: (a.name ?? "Account") as string,
        currency: (a.currency ?? "BRL") as string,
        timezone: (a.timezone ?? "America/Sao_Paulo") as string,
        status: (a.status ?? "active") as string,
        platform: (a.platform ?? "meta") as AdPlatform,
        access_token: (a.access_token ?? undefined) as string | undefined,
        created_at: (a.created_at ?? new Date().toISOString()) as string,
      }));

      setAccounts(allAccounts);
      if (allAccounts.length > 0) {
        const savedId = localStorage.getItem(STORAGE_KEY);
        const saved = allAccounts.find((a) => a.id === savedId);
        setActiveAccountState(saved ?? allAccounts[0]);
      } else {
        setActiveAccountState(null);
      }
    } catch {
      setAccounts([]);
      setActiveAccountState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  function setActiveAccount(account: AdAccount) {
    setActiveAccountState(account);
    localStorage.setItem(STORAGE_KEY, account.id);
  }

  return (
    <AccountContext.Provider
      value={{ accounts, activeAccount, setActiveAccount, isLoading, refetch: fetchAccounts }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error("useAccount must be used within AccountProvider");
  return ctx;
}
